/* eslint-disable arrow-parens */
/* eslint-disable */
import React, { useEffect, useState } from "react";
import "./NotificationModal.css";

export interface NotificationModalProps {
	isOpen: boolean;
	onClose: () => void; // X button + timer expiry — just closes
	onNavigate?: () => void; // card click — closes + navigates
	title: string;
	message: string;
	type?: "success" | "info" | "warning" | "error";
	duration?: number;
	position?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
}

export const NotificationModal: React.FC<NotificationModalProps> = ({
	isOpen,
	onClose,
	onNavigate,
	title,
	message,
	type = "success",
	duration = 20000,
	position = "bottom-right"
}) => {
	const [isVisible, setIsVisible] = useState(false);
	const [progress, setProgress] = useState(100);

	useEffect(() => {
		if (isOpen) {
			setProgress(100);
			const timer = setTimeout(() => setIsVisible(true), 50);
			return () => clearTimeout(timer);
		} else {
			setIsVisible(false);
		}
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen || duration <= 0) return;

		const startTime = Date.now();
		const endTime = startTime + duration;

		const updateProgress = () => {
			const now = Date.now();
			const remaining = Math.max(0, endTime - now);
			const newProgress = (remaining / duration) * 100;

			setProgress(newProgress);

			if (remaining > 0) {
				requestAnimationFrame(updateProgress);
			} else {
				onClose(); // ✅ timer just closes — no navigation
			}
		};

		const animationFrame = requestAnimationFrame(updateProgress);
		return () => cancelAnimationFrame(animationFrame);
	}, [isOpen, duration, onClose]);

	if (!isOpen) return null;

	const getIcon = () => {
		switch (type) {
			case "success":
				return (
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
						<polyline points="22 4 12 14.01 9 11.01" />
					</svg>
				);
			case "error":
				return (
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<circle cx="12" cy="12" r="10" />
						<line x1="15" y1="9" x2="9" y2="15" />
						<line x1="9" y1="9" x2="15" y2="15" />
					</svg>
				);
			case "warning":
				return (
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
						<line x1="12" y1="9" x2="12" y2="13" />
						<line x1="12" y1="17" x2="12.01" y2="17" />
					</svg>
				);
			case "info":
				return (
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<circle cx="12" cy="12" r="10" />
						<line x1="12" y1="16" x2="12" y2="12" />
						<line x1="12" y1="8" x2="12.01" y2="8" />
					</svg>
				);
			default:
				return (
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
						<polyline points="22 4 12 14.01 9 11.01" />
					</svg>
				);
		}
	};

	const getIconBg = () => {
		switch (type) {
			case "success":
				return "#dcfce7";
			case "error":
				return "#fee2e2";
			case "warning":
				return "#fef3c7";
			case "info":
				return "#dbeafe";
			default:
				return "#dcfce7";
		}
	};

	const getIconColor = () => {
		switch (type) {
			case "success":
				return "#16a34a";
			case "error":
				return "#dc2626";
			case "warning":
				return "#d97706";
			case "info":
				return "#2563eb";
			default:
				return "#16a34a";
		}
	};

	const positionClass = `notification-modal--${position}`;
	const visibleClass = isVisible ? "notification-modal--visible" : "";

	return (
		// ✅ Backdrop click does nothing — intentional
		<div className={`notification-modal ${positionClass} ${visibleClass}`}>
			{/* ✅ Card click navigates if onNavigate provided, else just closes */}
			<div
				className="notification-modal__content"
				onClick={onNavigate ?? onClose}
				role="button"
				tabIndex={0}
				style={{ cursor: onNavigate ? "pointer" : "default" }}
			>
				{/* Progress bar */}
				{duration > 0 && (
					<div className="notification-modal__progress-track">
						<div
							className="notification-modal__progress-bar"
							style={{
								width: `${progress}%`,
								backgroundColor: getIconColor()
							}}
						/>
					</div>
				)}

				<div className="notification-modal__wrapper">
					<div
						className="notification-modal__icon-ring"
						style={{
							backgroundColor: getIconBg(),
							color: getIconColor()
						}}
					>
						{getIcon()}
					</div>

					<div className="notification-modal__text">
						<h3 className="notification-modal__title">{title}</h3>
						<p className="notification-modal__message">{message}</p>
						{/* ✅ Hint text when card is clickable */}
						{onNavigate && (
							<span
								style={{ fontSize: "11px", color: getIconColor(), marginTop: "2px", display: "block" }}
							>
								Click to go to enrollment →
							</span>
						)}
					</div>

					{/* ✅ X button — stops propagation so card click doesn't fire */}
					<button
						className="notification-modal__close"
						onClick={(e) => {
							e.stopPropagation();
							onClose(); // just close, no navigation
						}}
						aria-label="Close notification"
					>
						<svg
							width="14"
							height="14"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<line x1="18" y1="6" x2="6" y2="18" />
							<line x1="6" y1="6" x2="18" y2="18" />
						</svg>
					</button>
				</div>
			</div>
		</div>
	);
};
